import { defineStore } from 'pinia';
import { computed } from 'vue';
import { type AgentDTO, AgentState, AgentStatus } from '@/data/Agent';
import { httpPatch } from '@/api/http';
import { V2_CALL_CENTER_AGENTS } from '@/constants/endpoint';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { AGENTS_QUERY_KEY, useAgents } from '@/api/useAgents';
import { useUserStore } from '@/stores/useUserStore';
import { getNowInSeconds } from '@/utils/dateUtils';
import { usePermissionsStore } from '@/stores/usePermissionsStore';

export const useAgentStore = defineStore('agentStore', () => {
  const queryClient = useQueryClient();
  const $permissionsStore = usePermissionsStore();
  const $userStore = useUserStore();
  const _isAgent = computed(() => !!$userStore._loggedInUser.agentUuid);
  const { _agents } = useAgents(_isAgent);

  const _agent = computed(() => {
    const agentUUID = $userStore._loggedInUser.agentUuid;
    return agentUUID ? _agents.value?.get(agentUUID) : undefined;
  });

  const updateAgentStatusMutation = useMutation({
    mutationFn: ({ status, comment }: { status: AgentStatus; comment?: string | null }) => {
      const agentUuid = _agent.value?.uuid;
      return httpPatch(`${V2_CALL_CENTER_AGENTS}/${agentUuid}/status`, { status, comment });
    },
    onMutate: async ({ status, comment }: { status: AgentStatus; comment?: string | null }) => {
      await queryClient.cancelQueries({ queryKey: [AGENTS_QUERY_KEY] });
      const oldAgents = queryClient.getQueryData([AGENTS_QUERY_KEY]);
      queryClient.setQueryData([AGENTS_QUERY_KEY], (agents: Map<string, AgentDTO>) => {
        const updatedAgents = new Map(agents);
        const agentToUpdate = _agent.value ? updatedAgents.get(_agent.value.uuid) : undefined;

        if (agentToUpdate) {
          const updatedAgent = {
            ...agentToUpdate,
            state: AgentState.WAITING,
            downtimeReason: status === 'On Break' ? ('BREAK' as const) : null,
            downtimeComment: $permissionsStore._hideCurrentBreakReason ? null : comment,
            lastStatusChange: getNowInSeconds(),
            status,
          };
          updatedAgents.set(agentToUpdate.uuid, updatedAgent);
        }

        return updatedAgents;
      });

      return { oldAgents };
    },
    onError: (err, agentData, context) => {
      queryClient.setQueryData([AGENTS_QUERY_KEY], context?.oldAgents);
    },
  });

  const updateOwnAgentStatus = (status: AgentStatus, comment?: string | null) => {
    updateAgentStatusMutation.mutate({ status, comment });
  };

  const _wrapUpActive = computed(() => _agent.value?.downtimeReason === 'WRAP_UP');

  return { updateOwnAgentStatus, _agent, _wrapUpActive };
});
